/* sb-dc-pngfit.js — keep the print file under the 45 MB ceiling, losslessly
   where that is possible. v2, 2026-09-04

   sb-dc-aug.js renders the print file, measures the PNG, and throws over
   45 MB ("the print file is too large (122MB)"). The cart module turns that
   into showError() and returns false — the "resolved false" a customer sees
   as a button that does nothing.

   v1 answered that by redrawing the canvas smaller. That works but spends
   print resolution, and most of the time it is spending it for no reason:
   canvas.toBlob always writes a 32-bit RGBA PNG with the browser's own
   choice of filtering, so a file that is really 40 MB of picture arrives as
   60 MB of container. Nothing about that is required.

   So the order is now:

     1. Palette. If the whole image uses 256 colours or fewer — which is most
        of what this shop prints: step and repeats, text banners, flat logo
        art — it is written as an 8-bit indexed PNG. Same pixels exactly,
        typically a third to a quarter of the size.
     2. Truecolour. Otherwise it is rewritten as RGB (alpha dropped when the
        image is fully opaque, which it is whenever the design has a
        background) with Paeth filtering on every row. Same pixels, usually
        25-40% smaller than what toBlob produced.
     3. Downscale. Only if neither of those fits does the canvas get redrawn
        smaller, exactly as v1 did.

   Both re-encoders are byte-exact: the PNG that comes out decodes to the
   same pixels that went in. Deflate comes from CompressionStream, which
   emits a zlib stream — which is what an IDAT chunk holds.

   A file that still will not fit after step 3's floor is handed back
   untouched so the original error can happen. Better a clear message than a
   print file nobody wants.

   Every intervention writes a print_downscale row to dc_events carrying
   which of the three paths ran, the before and after sizes, and — when the
   canvas was redrawn — the effective dpi that resulted. That is the number
   that says whether a file is still printable, and the thing to watch
   before deciding how far the 45 MB ceiling itself should move. */
(function () {
  'use strict';

  var LIMIT = 42 * 1024 * 1024;   // under the 45 MB throw, with room to spare
  var MIN_SCALE = 0.35;
  var MAX_PASSES = 6;
  var MAX_PIXELS = 40e6;          // above this, skip re-encoding (memory)

  var origToDataURL = HTMLCanvasElement.prototype.toDataURL;
  var origToBlob = HTMLCanvasElement.prototype.toBlob;
  var origFetch = window.fetch;

  window.__dcPngFit = { calls: 0, fixed: 0, last: null };

  /* ---------- dc_events, credentials lifted off the app's own call ---- */

  var REST = null;
  var queue = [];

  function headerBag(init, input) {
    var out = {};
    var h = (init && init.headers) || (input && input.headers) || null;
    if (!h) return out;
    try {
      if (typeof h.forEach === 'function' && !Array.isArray(h)) {
        h.forEach(function (v, k) { out[String(k).toLowerCase()] = v; });
      } else if (Array.isArray(h)) {
        h.forEach(function (p) { out[String(p[0]).toLowerCase()] = p[1]; });
      } else {
        Object.keys(h).forEach(function (k) { out[k.toLowerCase()] = h[k]; });
      }
    } catch (e) {}
    return out;
  }

  function post(rows) {
    if (!REST) return;
    try {
      origFetch.call(window, REST.url, {
        method: 'POST', headers: REST.headers,
        body: JSON.stringify(rows), keepalive: true
      }).catch(function () {});
    } catch (e) {}
  }

  function log(meta) {
    var row = { kind: 'print_downscale', ref: null, meta: meta || null };
    if (REST) post([row]); else if (queue.length < 20) queue.push(row);
  }

  window.fetch = function (input, init) {
    try {
      if (!REST) {
        var url = (typeof input === 'string') ? input : (input && input.url) || '';
        var m = /^(https:\/\/[a-z0-9]+\.supabase\.co)\/rest\/v1\//.exec(url);
        if (m) {
          var h = headerBag(init, input);
          if (h.apikey) {
            REST = {
              url: m[1] + '/rest/v1/dc_events',
              headers: {
                'Content-Type': 'application/json',
                Prefer: 'return=minimal',
                apikey: h.apikey,
                Authorization: h.authorization || ('Bearer ' + h.apikey)
              }
            };
            if (queue.length) post(queue.splice(0, queue.length));
          }
        }
      }
    } catch (e) {}
    return origFetch.apply(this, arguments);
  };

  /* ================= PNG writer ======================================= */

  var CRC = (function () {
    var t = new Int32Array(256);
    for (var n = 0; n < 256; n++) {
      var c = n;
      for (var k = 0; k < 8; k++) c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
      t[n] = c;
    }
    return t;
  })();

  function crc32(bytes) {
    var c = -1;
    for (var i = 0; i < bytes.length; i++) c = CRC[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
    return (c ^ -1) >>> 0;
  }

  function chunk(type, data) {
    var out = new Uint8Array(12 + data.length);
    var dv = new DataView(out.buffer);
    dv.setUint32(0, data.length);
    for (var i = 0; i < 4; i++) out[4 + i] = type.charCodeAt(i);
    out.set(data, 8);
    var forCrc = out.subarray(4, 8 + data.length);
    dv.setUint32(8 + data.length, crc32(forCrc));
    return out;
  }

  function ihdr(w, h, colorType) {
    var d = new Uint8Array(13);
    var dv = new DataView(d.buffer);
    dv.setUint32(0, w);
    dv.setUint32(4, h);
    d[8] = 8;            // bit depth
    d[9] = colorType;    // 2 = RGB, 3 = palette, 6 = RGBA
    d[10] = 0; d[11] = 0; d[12] = 0;
    return d;
  }

  function deflate(bytes) {
    /* CompressionStream('deflate') produces a zlib stream, which is exactly
       what PNG stores in IDAT. Not available everywhere; the caller treats a
       rejection as "cannot re-encode" and moves on. */
    if (typeof CompressionStream !== 'function') return Promise.reject(new Error('no cs'));
    var cs = new CompressionStream('deflate');
    var w = cs.writable.getWriter();
    w.write(bytes);
    w.close();
    return new Response(cs.readable).arrayBuffer().then(function (buf) {
      return new Uint8Array(buf);
    });
  }

  function assemble(parts) {
    var total = 8;
    parts.forEach(function (p) { total += p.length; });
    var out = new Uint8Array(total);
    out.set([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a], 0);
    var at = 8;
    parts.forEach(function (p) { out.set(p, at); at += p.length; });
    return new Blob([out], { type: 'image/png' });
  }

  /* ---------- pixel inspection ---------------------------------------- */

  /* One pass that answers both questions we need: is anything transparent,
     and are there few enough distinct colours to index. Gives up counting
     the moment it passes 256, so a photograph costs one cheap scan. */
  function inspect(data) {
    var opaque = true;
    var seen = new Map();
    var tooMany = false;
    for (var i = 0; i < data.length; i += 4) {
      if (data[i + 3] !== 255) { opaque = false; }
      if (!tooMany) {
        var key = (data[i] << 16) | (data[i + 1] << 8) | data[i + 2];
        if (!seen.has(key)) {
          if (seen.size >= 256) tooMany = true;
          else seen.set(key, seen.size);
        }
      }
      if (tooMany && !opaque) break;   // nothing left to learn
    }
    return { opaque: opaque, palette: tooMany ? null : seen };
  }

  /* ---------- filtering ------------------------------------------------ */

  function paeth(a, b, c) {
    var p = a + b - c;
    var pa = p > a ? p - a : a - p;
    var pb = p > b ? p - b : b - p;
    var pc = p > c ? p - c : c - p;
    if (pa <= pb && pa <= pc) return a;
    return pb <= pc ? b : c;
  }

  /* Truecolour rows, Paeth filtered. bpp is 3 or 4. */
  function filterTruecolour(data, w, h, bpp) {
    var rowBytes = w * bpp;
    var out = new Uint8Array((rowBytes + 1) * h);
    var cur = new Uint8Array(rowBytes);
    var prev = new Uint8Array(rowBytes);
    var at = 0;
    for (var y = 0; y < h; y++) {
      var src = y * w * 4;
      if (bpp === 4) {
        cur.set(data.subarray(src, src + rowBytes));
      } else {
        for (var x = 0, d = 0; x < w; x++, src += 4) {
          cur[d++] = data[src];
          cur[d++] = data[src + 1];
          cur[d++] = data[src + 2];
        }
      }
      out[at++] = 4;                        // Paeth
      for (var i = 0; i < rowBytes; i++) {
        var a = i >= bpp ? cur[i - bpp] : 0;
        var b = prev[i];
        var c = i >= bpp ? prev[i - bpp] : 0;
        out[at++] = (cur[i] - paeth(a, b, c)) & 0xff;
      }
      var t = prev; prev = cur; cur = t;
    }
    return out;
  }

  /* Indexed rows. Palette indices do not benefit from prediction — the
     numbers are labels, not magnitudes — so these go out unfiltered. */
  function filterIndexed(data, w, h, palette) {
    var out = new Uint8Array((w + 1) * h);
    var at = 0;
    for (var y = 0; y < h; y++) {
      out[at++] = 0;
      var src = y * w * 4;
      for (var x = 0; x < w; x++, src += 4) {
        out[at++] = palette.get((data[src] << 16) | (data[src + 1] << 8) | data[src + 2]);
      }
    }
    return out;
  }

  /* ---------- the two lossless encoders -------------------------------- */

  function encodeIndexed(data, w, h, palette) {
    var plte = new Uint8Array(palette.size * 3);
    palette.forEach(function (idx, key) {
      plte[idx * 3] = (key >> 16) & 0xff;
      plte[idx * 3 + 1] = (key >> 8) & 0xff;
      plte[idx * 3 + 2] = key & 0xff;
    });
    return deflate(filterIndexed(data, w, h, palette)).then(function (z) {
      return assemble([chunk('IHDR', ihdr(w, h, 3)), chunk('PLTE', plte),
                       chunk('IDAT', z), chunk('IEND', new Uint8Array(0))]);
    });
  }

  function encodeTruecolour(data, w, h, opaque) {
    var bpp = opaque ? 3 : 4;
    return deflate(filterTruecolour(data, w, h, bpp)).then(function (z) {
      return assemble([chunk('IHDR', ihdr(w, h, opaque ? 2 : 6)),
                       chunk('IDAT', z), chunk('IEND', new Uint8Array(0))]);
    });
  }

  /* Try the lossless paths in order of how much they usually save. Resolves
     with {blob, path} or null when neither helps. */
  function lossless(source) {
    return new Promise(function (resolve) {
      try {
        var w = source.width, h = source.height;
        if (w * h > MAX_PIXELS) return resolve(null);
        var ctx = source.getContext('2d');
        if (!ctx) return resolve(null);
        var data = ctx.getImageData(0, 0, w, h).data;
        var look = inspect(data);

        var attempt = look.palette && look.palette.size <= 256
          ? encodeIndexed(data, w, h, look.palette).then(function (b) {
              return { blob: b, path: 'palette' };
            })
          : Promise.reject(new Error('not indexable'));

        attempt.catch(function () {
          return encodeTruecolour(data, w, h, look.opaque).then(function (b) {
            return { blob: b, path: look.opaque ? 'rgb' : 'rgba' };
          });
        }).then(function (r) {
          resolve(r && r.blob && r.blob.size <= LIMIT ? r : null);
        }).catch(function () { resolve(null); });
      } catch (e) {
        resolve(null);
      }
    });
  }

  /* ---------- downscaling (unchanged from v1) -------------------------- */

  function scaled(source, scale) {
    var c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(source.width * scale));
    c.height = Math.max(1, Math.round(source.height * scale));
    var g = c.getContext('2d');
    try {
      g.imageSmoothingEnabled = true;
      g.imageSmoothingQuality = 'high';
    } catch (e) {}
    g.drawImage(source, 0, 0, c.width, c.height);
    return c;
  }

  function nextScale(scale, bytes) {
    return Math.max(MIN_SCALE, scale * Math.sqrt(LIMIT / bytes) * 0.97);
  }

  /* ---------- reporting ------------------------------------------------ */

  function note(source, path, before, after, scale, passes) {
    var meta = {
      path: path, before: before, after: after,
      w: source.width, h: source.height
    };
    if (path === 'downscale') {
      meta.scale = Math.round(scale * 1000) / 1000;
      meta.passes = passes;
      meta.outW = Math.round(source.width * scale);
      meta.outH = Math.round(source.height * scale);
    }
    window.__dcPngFit.fixed++;
    window.__dcPngFit.last = meta;
    try {
      var s = window.__dcApi && window.__dcApi.spec;
      if (s) {
        meta.specW = s.widthIn || null;
        meta.specH = s.heightIn || null;
        var longIn = Math.max(s.widthIn || 0, s.heightIn || 0);
        var longPx = Math.max(meta.outW || source.width, meta.outH || source.height);
        if (longIn > 0) meta.dpi = Math.round(longPx / longIn);
      }
      var p = window.__dcSelectedProduct;
      if (p && p.handle) meta.handle = p.handle;
    } catch (e) {}
    log(meta);
  }

  function isPngType(t) {
    return !t || String(t).toLowerCase() === 'image/png';
  }

  /* Shared tail: given an oversized PNG blob for `source`, produce something
     that fits, or null to leave the original alone. */
  function fit(source, blob) {
    var before = blob.size;
    return lossless(source).then(function (r) {
      if (r) {
        note(source, r.path, before, r.blob.size, 1, 0);
        return r.blob;
      }
      return new Promise(function (resolve) {
        var scale = 1, passes = 0;
        var step = function (current) {
          if (current.size <= LIMIT) {
            note(source, 'downscale', before, current.size, scale, passes);
            return resolve(current);
          }
          if (passes >= MAX_PASSES || scale <= MIN_SCALE) return resolve(null);
          var next = nextScale(scale, current.size);
          if (next >= scale) return resolve(null);
          scale = next;
          passes++;
          origToBlob.call(scaled(source, scale), function (b) {
            if (!b) return resolve(null);
            step(b);
          }, 'image/png');
        };
        step(blob);
      });
    }).catch(function () { return null; });
  }

  /* ---------- toBlob --------------------------------------------------- */

  HTMLCanvasElement.prototype.toBlob = function (cb, type) {
    var source = this;
    if (!isPngType(type) || typeof cb !== 'function') {
      return origToBlob.apply(this, arguments);
    }
    return origToBlob.call(this, function (blob) {
      try {
        window.__dcPngFit.calls++;
        if (!blob || blob.size <= LIMIT) return cb(blob);
        fit(source, blob).then(function (out) { cb(out || blob); });
      } catch (e) {
        cb(blob);
      }
    }, type);
  };

  /* ---------- toDataURL ------------------------------------------------
     Synchronous by contract, so the lossless encoders (async, they go
     through CompressionStream) cannot be used here. Anything reaching this
     path oversized gets the v1 treatment. In practice the print file goes
     out through toBlob; this is the safety net. */

  function dataUrlBytes(url) {
    var c = url.indexOf(',');
    if (c < 0) return 0;
    var b64 = url.length - c - 1;
    var pad = url.charCodeAt(url.length - 1) === 61
      ? (url.charCodeAt(url.length - 2) === 61 ? 2 : 1) : 0;
    return Math.floor(b64 * 3 / 4) - pad;
  }

  HTMLCanvasElement.prototype.toDataURL = function (type) {
    var url = origToDataURL.apply(this, arguments);
    try {
      if (!isPngType(type)) return url;
      var bytes = dataUrlBytes(url);
      window.__dcPngFit.calls++;
      if (bytes <= LIMIT) return url;

      var scale = 1, out = url, passes = 0, before = bytes;
      while (bytes > LIMIT && passes < MAX_PASSES) {
        var next = nextScale(scale, bytes);
        if (next >= scale) break;
        scale = next;
        passes++;
        out = origToDataURL.call(scaled(this, scale), 'image/png');
        bytes = dataUrlBytes(out);
        if (scale <= MIN_SCALE) break;
      }
      if (bytes > LIMIT) return url;
      note(this, 'downscale', before, bytes, scale, passes);
      return out;
    } catch (e) {
      return url;
    }
  };
})();
